import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  AiFollowupSettingInput,
  AiFollowupSettingView,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

const KEY = ['settings', 'ai-followup'];

export function useAiFollowupSetting(options?: { pollUntilNewRunAfter?: number | null }) {
  const target = options?.pollUntilNewRunAfter ?? null;
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<AiFollowupSettingView> => {
      const res = await apiClient.get<ApiSuccess<AiFollowupSettingView>>('/settings/ai-followup');
      return res.data.data;
    },
    // 試跑後短期輪詢:若 target(enqueuedAt)已被設定且還沒看到更新的 lastRun → 3s 重抓;
    // 最長 60s 內仍未更新則停止(避免無限輪詢)
    refetchInterval: (query) => {
      if (target == null) return false;
      const now = Date.now();
      if (now - target > 60_000) return false;
      const lastRunAt = query.state.data?.lastRun?.runAt;
      const lastRunTs = lastRunAt ? new Date(lastRunAt).getTime() : 0;
      if (lastRunTs > target) return false; // 已看到新一筆
      return 3000;
    },
  });
}

export function useUpdateAiFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AiFollowupSettingInput): Promise<AiFollowupSettingView> => {
      const res = await apiClient.put<ApiSuccess<AiFollowupSettingView>>('/settings/ai-followup', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useRunAiFollowupNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ enqueued: true }> => {
      const res = await apiClient.post<ApiSuccess<{ enqueued: true }>>('/settings/ai-followup/run-now');
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
